import { CommonModule } from '@angular/common';
import {
  OnDestroy,
  OnInit} from '@angular/core';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject
} from '@angular/core';
import { Router} from '@angular/router';
import { NavigationEnd } from '@angular/router';
import {
  MsalGuardConfiguration,
  MsalBroadcastService,
  MsalService} from '@azure/msal-angular';
import {
  MSAL_GUARD_CONFIG
} from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { RdsModule } from '@rabobank/rds-angular';
import { Subject, filter, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BatchService } from '../../services/batch.service';
import { BatchStateService } from '../../services/batch-state.service';
import { FormsModule } from '@angular/forms';
import { IdleTimeoutService } from '../../services/idle-timeout.service';
import { GraphService } from '../../services/graph.service';
import { AuthorizationService } from '../../services/authorization.service';
import { ConnectorOption } from '../../models/batch.interface';

// use ConnectorOption from models (value + viewValue)

@Component({
  selector: 'app-headers',
  imports: [CommonModule, RdsModule, FormsModule],
  templateUrl: './headers.component.html',
  styleUrls: ['./headers.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HeadersComponent implements OnInit, OnDestroy {
  public isUserLoggedIn: boolean = false;
  public selectedProfile: string = '';
  public availableProfiles: ConnectorOption[] = [];
  public isAdmin: boolean = false;
  public isOnBatchRoute: boolean = false;
  private readonly _destroy = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalBroadcastService: MsalBroadcastService,
    private msalService: MsalService,
    private sharedService: BatchService,
    private batchStateService: BatchStateService,
    private router: Router,
    private idletimeout: IdleTimeoutService,
    private graphService: GraphService
    ,
    private authorizationService: AuthorizationService
  ) {}

  public ngOnInit(): void {
    this.isOnBatchRoute = this.router.url.includes('/batches');
    this.syncSelectedConnectorFromUrl(this.router.url);
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._destroy)
      )
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects || event.url;
        this.isOnBatchRoute = url.includes('/batches');
        this.syncSelectedConnectorFromUrl(url);
      });
    this.sharedService.selectedConnector
      .pipe(takeUntil(this._destroy))
      .subscribe((connector: string | null) => {
        if (connector) {
          this.selectedProfile = connector;
          this.navigateToConnector(connector);
        }
      });
    void this.refreshHeaderState();

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (interactionStatus: InteractionStatus) =>
            interactionStatus === InteractionStatus.None
        ),
        takeUntil(this._destroy)
      )
      .subscribe(() => {
        void this.refreshHeaderState();
      });
  }

  // Authorization logic moved to AuthorizationService

  public login(): void {
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }
  }

  public logout(): void {
    sessionStorage.clear();
    localStorage.clear();
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: environment.azureAd.posLogoutUrl,
    });
  }

  public ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
  }

  private async refreshHeaderState(): Promise<void> {
    console.log(
      '[Headers] checking account and group claims.'
    );
    const accounts = this.msalService.instance.getAllAccounts();
    this.isUserLoggedIn = accounts.length > 0;
    console.log(
      '[Headers] accounts found:',
      accounts.length,
      accounts.map((a) => a.username || a.homeAccountId)
    );
    this.sharedService.isUserLoggedIn.next(this.isUserLoggedIn);
    if (this.isUserLoggedIn) {
      this.idletimeout.start();
    } else {
      this.idletimeout.stop();
    }

    if (accounts.length === 0) {
      this.login();
      return;
    }

    const account = this.msalService.instance.getActiveAccount() || accounts[0];
    this.msalService.instance.setActiveAccount(account);

    this.msalService
      .acquireTokenSilent({
        account,
        scopes: environment.scopes,
      })
      .subscribe({
        next: () => {},
        error: (err) => console.error('Token refresh error:', err),
      });

    console.log(
      '[Headers] active account:',
      account?.username || account?.homeAccountId
    );
    console.log(
      '[Headers] resolving authorization via Microsoft Graph membership check.'
    );

    try {
      const result = await this.authorizationService.evaluateAuthorization(account);
      this.isAdmin = result.isAdmin;
      if (!result.isAuthorized) {
        console.warn(
          'User is not member of any connector groups and is not admin, signing out.'
        );
        this.msalService.logoutRedirect({
          postLogoutRedirectUri: environment.azureAd.posLogoutUrl,
        });
        return;
      }
    } catch (e) {
      console.error('Failed to evaluate allowed groups', e);
      this.isAdmin = false;
      return;
    }

    await this.loadAvailableProfiles();

    const current = this.sharedService.selectedConnector.value || this.getConnectorFromUrl(this.router.url);
    if (current) {
      this.selectedProfile = current;
      this.sharedService.selectedConnector.next(current);
      return;
    }

    if (!this.isAdmin && this.availableProfiles.length === 0) {
      console.warn('User is not authorized to access any application.');
    }
  }

  private async loadAvailableProfiles(): Promise<void> {
    // Define all available applications
    // We're going live with Agency Desk (AG_DESK) only.
    // Other profiles are commented out temporarily for the release.
    const allProfiles: ConnectorOption[] = [
      /* { value: 'HK_TRADE', viewValue: 'HK Trade' }, */
      { value: 'AG_DESK', viewValue: 'Agency Desk' },
      { value: 'PFEU', viewValue: 'Project Finance - Europe' },
      { value: 'VCF', viewValue: 'Value Chain Finance' },
      // { value: 'PFNA', label: 'Project Finance - NA' }
      /* { value: 'GNM', viewValue: 'GNM' },
      { value: 'FEES_MGMT', viewValue: 'Fees Management' },
      { value: 'HK_LC', viewValue: 'HK LC' } */
    ];

    if (this.isAdmin) {
      // Admin users can see all applications
      this.availableProfiles = allProfiles;
      return;
    }

    console.log('[Headers] loadAvailableProfiles start');
    // For non-admin users, compute available profiles from the connector->group mapping
    const account = this.msalService.instance.getActiveAccount();
    const groups = account?.idTokenClaims?.['groups'] as string[] | undefined;

    const env = environment as unknown as {
      connectorGroupMap?: { [connector: string]: string[] };
    };
    const connectorGroupMap = env.connectorGroupMap as
      | { [connector: string]: string[] }
      | undefined;
    const userId = account?.idTokenClaims?.['oid'] as string | undefined;

    if (
      !(connectorGroupMap && Object.keys(connectorGroupMap).length > 0) &&
      (!groups || groups.length === 0) &&
      !userId
    ) {
      // No mapping or no identifying claims: show no profiles
      this.availableProfiles = [];
      return;
    }

    // Delegate to AuthorizationService to compute available connectors
    try {
      this.availableProfiles = await this.authorizationService.getAvailableConnectors(
        this.msalService.instance.getActiveAccount(),
        allProfiles
      );
      console.log(
        '[Headers] availableProfiles (computed):',
        this.availableProfiles.map((p) => p.value)
      );
    } catch (e) {
      console.warn('Failed to compute available profiles via AuthorizationService', e);
      this.availableProfiles = [];
    }
  }

  public onProfileChange(connector: string): void {
    if (connector) {
      // Clear any persisted checked batches so switching profiles does not retain selections
      try {
        const current = this.batchStateService.getState();
        this.batchStateService.saveState({
          ...current,
          checkedBatches: [],
          selectedBatch: undefined,
        });
      } catch (_e) {
        /* ignore */
      }

      this.sharedService.selectedConnector.next(connector);
      this.navigateToConnector(connector);
    }
  }

  private syncSelectedConnectorFromUrl(url: string): void {
    const connector = this.getConnectorFromUrl(url);
    if (!connector) {
      return;
    }

    this.selectedProfile = connector;
    if (this.sharedService.selectedConnector.value !== connector) {
      this.sharedService.selectedConnector.next(connector);
    }
  }

  private getConnectorFromUrl(url: string): string | null {
    const normalizedUrl = (url || '').toLowerCase();
    if (normalizedUrl.includes('/batches/agency-desk')) return 'AG_DESK';
    if (normalizedUrl.includes('/batches/pf-eu')) return 'PFEU';
    if (normalizedUrl.includes('/batches/pf-na')) return 'PFNA';
    if (normalizedUrl.includes('/batches/vcf')) return 'VCF';
    if (normalizedUrl.includes('/batches/hk-trade')) return 'HK_TRADE';
    if (normalizedUrl.includes('/batches/gnm')) return 'GNM';
    if (normalizedUrl.includes('/batches/fees')) return 'FEES_MGMT';
    if (normalizedUrl.includes('/batches/hk-lc')) return 'HK_LC';
    return null;
  }

  private navigateToConnector(connector: string): void {
    switch (connector) {
      case 'HK_TRADE':
        this.router.navigate(['/batches/hk-trade']);
        break;
      case 'AG_DESK':
        this.router.navigate(['/batches/agency-desk']);
        break;
      case 'PFEU':
        this.router.navigate(['/batches/pf-eu']);
        break;
      case 'PFNA':
        this.router.navigate(['/batches/pf-na']);
        break;
      case 'GNM':
        this.router.navigate(['/batches/gnm']);
        break;
      case 'FEES_MGMT':
        this.router.navigate(['/batches/fees']);
        break;
      case 'HK_LC':
        this.router.navigate(['/batches/hk-lc']);
        break;
      case 'VCF':
        this.router.navigate(['/batches/vcf']);
        break;
      default:
        this.router.navigate(['/batches']);
    }
  }
}
